import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { createSessionWithPassword, updateSessionProject, getMySessionList } from '@/api/reviewApi.ts';
import { isSupabaseConfigured } from '@/api/supabaseClient.ts';
import { capabilities } from '@/capabilities.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { gatherExportData } from '@/export/htmlExporter.ts';
import { getUploadToken } from '@/api/publishApi.ts';
import { dataUrlToBytes } from '@/utils/dataUrlUtils.ts';
import { uploadToBlob, concurrentMap } from '@/export/publishOrchestrator.ts';
import { interceptEmbeddedSignInForSharing } from '@/components/AiWorkspace/embeddedAuthGate.ts';
import { env } from '@/config/environment.ts';
import styles from './ShareDialog.module.css';

/** Share link host — Dev points at share-dev.previspro.com, Prod at share.previspro.com */
const REVIEW_BASE_URL = `https://${env.shareDomain}`;

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
}

const TTL_OPTIONS = [
  { days: 7, labelKey: 'ttl7Days' },
  { days: 30, labelKey: 'ttl30Days' },
  { days: 90, labelKey: 'ttl90Days' },
];

type DialogMode = 'checking' | 'create' | 'syncing' | 'sync-error' | 'confirm-new' | 'done';

// Session history stored in localStorage (includes password so user can reshare)
interface SessionHistoryEntry { id: string; title: string; url: string; password?: string; createdAt: string; expiresAt: string }

function getSessionHistory(): SessionHistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem('review-session-history') || '[]');
  } catch {
    return [];
  }
}

function addToSessionHistory(entry: SessionHistoryEntry) {
  const history = getSessionHistory();
  // Update existing entry or prepend new (preserve password from prior entry)
  const idx = history.findIndex(h => h.id === entry.id);
  if (idx >= 0) {
    if (!entry.password && history[idx].password) entry.password = history[idx].password;
    history[idx] = entry;
  } else {
    history.unshift(entry);
  }
  // Keep max 20 entries
  localStorage.setItem('review-session-history', JSON.stringify(history.slice(0, 20)));
}

function getStoredPassword(sessionId: string): string | null {
  const history = getSessionHistory();
  return history.find(h => h.id === sessionId)?.password ?? null;
}

/** Look up stored password by matching the share URL (fallback when sessionId is unavailable). */
function getStoredPasswordByUrl(url: string): string | null {
  const history = getSessionHistory();
  return history.find(h => h.url === url)?.password ?? null;
}

/** Cache fetched revision per session so we only hit the server once per app session. */
let cachedRevision: { sessionId: string; revision: number } | null = null;

export function ShareDialog({ open, onClose }: ShareDialogProps) {
  const { t } = useTranslation('sharing');
  const project = useProjectStore(s => s.project);
  const reviewSessionId = useProjectStore(s => s.reviewSessionId);
  const setReviewSessionId = useProjectStore(s => s.setReviewSessionId);
  const hasTeamAccess = useProjectStore(s => s.hasTeamAccess);
  const teamAccessUrl = useProjectStore(s => s.teamAccessUrl);
  const teamRevision = useProjectStore(s => s.teamRevision);
  const teamSharePassword = useProjectStore(s => s.teamSharePassword);

  const [mode, setMode] = useState<DialogMode>('checking');
  const [ttlDays, setTtlDays] = useState(7);
  const [isBusy, setIsBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharePassword, setSharePassword] = useState<string | null>(null);
  const [revision, setRevision] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [progressMsg, setProgressMsg] = useState<string | null>(null);
  const [wasSynced, setWasSynced] = useState(false);
  const [showRewardHint, setShowRewardHint] = useState(false);
  const [resumedExisting, setResumedExisting] = useState(false);


  // On open: check if we have an existing session
  useEffect(() => {
    if (!open) return;

    // Reset state
    setShareUrl(null);
    setSharePassword(null);
    setError(null);
    setCopied(false);
    setProgressMsg(null);
    setWasSynced(false);
    setResumedExisting(false);
    // Show reward hint if user is signed in and has never shared any project before
    setShowRewardHint(false);
    if (useAccountStore.getState().accountUser && getSessionHistory().length === 0) {
      setShowRewardHint(true);
    }

    if (capabilities.bridge) {
      // Embedded mode — use team access state from projectStore
      if (hasTeamAccess && teamAccessUrl) {
        setShareUrl(teamAccessUrl);
        // Password from SQLite (sent by Unity via teamAccessState on project load),
        // with localStorage as fallback for legacy sessions
        const teamSessionId = useProjectStore.getState().teamSessionId;
        const pw = teamSharePassword
          ?? (teamSessionId ? getStoredPassword(teamSessionId) : null)
          ?? getStoredPasswordByUrl(teamAccessUrl);
        setSharePassword(pw);
        setResumedExisting(true);

        // Use cached revision if available, otherwise fetch from server once
        if (cachedRevision && cachedRevision.sessionId === teamSessionId) {
          setRevision(cachedRevision.revision);
          setMode('done');
        } else if (teamSessionId) {
          setMode('checking');
          getMySessionList()
            .then(sessions => {
              const existing = sessions.find(s => s.id === teamSessionId);
              const rev = existing?.revision ?? (teamRevision || 1);
              cachedRevision = { sessionId: teamSessionId, revision: rev };
              setRevision(rev);
              setMode('done');
            })
            .catch(() => {
              setRevision(teamRevision || 1);
              setMode('done');
            });
        } else {
          setRevision(teamRevision || 1);
          setMode('done');
        }
      } else {
        setMode('create');
      }
      return;
    }

    if (!reviewSessionId) {
      setMode('create');
      return;
    }

    // Check if existing session is still valid — auto-sync if so
    setMode('checking');
    getMySessionList()
      .then(sessions => {
        const existing = sessions.find(s => s.id === reviewSessionId);
        if (existing && !existing.revoked && new Date(existing.expires_at) > new Date()) {
setRevision(existing.revision);
          setShareUrl(`${REVIEW_BASE_URL}?session=${reviewSessionId}`);
          setSharePassword(getStoredPassword(reviewSessionId!));
          setResumedExisting(true);
          setMode('done');
        } else {
          // Session gone/expired/revoked — reset and go to create
          setReviewSessionId(null);
          setMode('create');
        }
      })
      .catch(() => {
        // Can't reach server — fall back to create
        setReviewSessionId(null);
        setMode('create');
      });
  }, [open, reviewSessionId, setReviewSessionId, hasTeamAccess, teamAccessUrl, teamRevision, teamSharePassword]);

  const buildReviewPayload = useCallback(async () => {
    setProgressMsg(t('progressPreparingData'));
    // includeDoodles: true — this payload feeds the LIVE review app (ReviewLoader merges these
    // comments as its base), which has always rendered doodles; the opt-out checkbox gates only
    // the static HTML player.
    const exportData = await gatherExportData(false, true, true);

    // Collect thumbnails that are data URLs (not already CDN URLs)
    const thumbnails: { sceneIdx: number; shotIdx: number; dataUrl: string }[] = [];
    for (let si = 0; si < exportData.scenes.length; si++) {
      for (let shi = 0; shi < exportData.scenes[si].shots.length; shi++) {
        const shot = exportData.scenes[si].shots[shi];
        if (shot.thumbnailDataUrl?.startsWith('data:')) {
          thumbnails.push({ sceneIdx: si, shotIdx: shi, dataUrl: shot.thumbnailDataUrl });
        }
      }
    }

    // Upload thumbnails to Vercel Blob if any exist
    if (thumbnails.length > 0) {
      setProgressMsg(t('progressUploadingThumbnails', { current: 0, total: thumbnails.length }));
      const { clientToken, pathPrefix } = await getUploadToken();

      await concurrentMap(thumbnails, 5, async (thumb, idx) => {
        const { bytes, mimeType } = dataUrlToBytes(thumb.dataUrl);
        const ext = mimeType === 'image/jpeg' ? 'jpg' : 'png';
        const pathname = `${pathPrefix}thumb_${thumb.sceneIdx}_${thumb.shotIdx}.${ext}`;
        const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);

        // Replace data URL with CDN URL
        exportData.scenes[thumb.sceneIdx].shots[thumb.shotIdx].thumbnailDataUrl = blobUrl;

        setProgressMsg(t('progressUploadingThumbnails', { current: idx + 1, total: thumbnails.length }));
      });
    }

    // Upload shot videos (blob:/file:// URLs from store → persistent Vercel Blob CDN).
    // Sourced from shot.videoObjectUrl, NOT projectStore.videoShots: that array is built only from
    // generation-history rows, so an animatic (pushed by Unity as a bare file:// URL on sceneReady,
    // history-row-free) is absent from it and the review payload dropped every one. The row is kept
    // as a MIME hint only — same substitution gatherExportData makes.
    const storeShotVideo = new Map<number, string>();
    for (const scene of useProjectStore.getState().project?.scenes ?? []) {
      for (const shot of scene.shots) {
        if (shot.videoObjectUrl) storeShotVideo.set(shot.id, shot.videoObjectUrl);
      }
    }
    const storeVideoShots = useProjectStore.getState().videoShots;
    const shotVideoEntries: { sceneIdx: number; shotIdx: number; objectUrl: string; mimeType: string }[] = [];
    for (let si = 0; si < exportData.scenes.length; si++) {
      for (let shi = 0; shi < exportData.scenes[si].shots.length; shi++) {
        const shot = exportData.scenes[si].shots[shi];
        const objectUrl = storeShotVideo.get(shot.id);
        if (objectUrl) {
          const hintMime = storeVideoShots.find(v => v.shotId === shot.id)?.mimeType;
          shotVideoEntries.push({ sceneIdx: si, shotIdx: shi, objectUrl, mimeType: hintMime ?? 'video/mp4' });
        }
      }
    }

    if (shotVideoEntries.length > 0) {
      setProgressMsg(t('progressUploadingVideos', { current: 0, total: shotVideoEntries.length }));
      const { clientToken, pathPrefix } = await getUploadToken();

      await concurrentMap(shotVideoEntries, 2, async (vid, idx) => {
        const resp = await fetch(vid.objectUrl);
        const blob = await resp.blob();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const mimeType = blob.type || vid.mimeType || 'video/mp4';
        const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
        const pathname = `${pathPrefix}video_${vid.sceneIdx}_${vid.shotIdx}.${ext}`;
        const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);

        exportData.scenes[vid.sceneIdx].shots[vid.shotIdx].videoDataUrl = blobUrl;

        setProgressMsg(t('progressUploadingVideos', { current: idx + 1, total: shotVideoEntries.length }));
      });
    }

    // Upload continuous transition videos (blob: URLs → persistent Vercel Blob CDN)
    const transitionVideos: { idx: number; url: string }[] = [];
    for (let i = 0; i < exportData.transitions.length; i++) {
      const t = exportData.transitions[i];
      if (t.videoObjectUrl) {
        transitionVideos.push({ idx: i, url: t.videoObjectUrl });
      }
    }

    if (transitionVideos.length > 0) {
      setProgressMsg(t('progressUploadingTransitionVideos', { current: 0, total: transitionVideos.length }));
      const { clientToken, pathPrefix } = await getUploadToken();

      await concurrentMap(transitionVideos, 2, async (tv, idx) => {
        const resp = await fetch(tv.url);
        const blob = await resp.blob();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const mimeType = blob.type || 'video/mp4';
        const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
        const pathname = `${pathPrefix}trans_video_${idx}.${ext}`;
        const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);

        // Replace blob: URL with persistent CDN URL
        exportData.transitions[tv.idx].videoObjectUrl = blobUrl;

        setProgressMsg(t('progressUploadingTransitionVideos', { current: idx + 1, total: transitionVideos.length }));
      });
    }

    // Upload audio clips to Vercel Blob (base64 data → CDN URL)
    const audioWithData = exportData.audioClips.filter(c => c.rawBase64);
    if (audioWithData.length > 0) {
      setProgressMsg(t('progressUploadingAudio', { current: 0, total: audioWithData.length }));
      const { clientToken, pathPrefix } = await getUploadToken();

      await concurrentMap(audioWithData, 3, async (clip, idx) => {
        const { bytes, mimeType } = dataUrlToBytes(clip.rawBase64!);
        const ext = mimeType.includes('webm') ? 'webm' : mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp3') || mimeType.includes('mpeg') ? 'mp3' : 'wav';
        const pathname = `${pathPrefix}audio_${clip.id}.${ext}`;
        const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);

        // Replace base64 with CDN URL
        clip.rawBase64 = null;
        clip.mediaUrl = blobUrl;

        setProgressMsg(t('progressUploadingAudio', { current: idx + 1, total: audioWithData.length }));
      });
    }

    setProgressMsg(t('progressCreatingSession'));
    return exportData;
  }, [t]);

  const handleCreate = useCallback(async () => {
    if (!project) return;
    // Team Access needs a signed-in account: ask Unity for the forced sign-in NOW, not after a
    // regeneration pass the uploader would refuse anyway (owner 2026-09-24).
    if (interceptEmbeddedSignInForSharing()) return;

    setIsBusy(true);
    setError(null);

    try {
      if (capabilities.bridge) {
        // Embedded mode — upload via Unity team pipeline
        const shareStart = performance.now();
        console.log('[ShareDialog] handleCreate: starting embedded share...');
        const { requestTeamUpload } = await import('@/bridge/embeddedBridge.ts');
        const result = await requestTeamUpload(ttlDays);
        console.log(`[ShareDialog] handleCreate: share complete in ${(performance.now() - shareStart).toFixed(0)}ms`);

        // Use ?session= URL for reviewers (not ?team= which requires app auth)
        const reviewUrl = `${REVIEW_BASE_URL}?session=${result.sessionId}`;
        setShareUrl(reviewUrl);
        setSharePassword(result.password);
        setRevision(result.revision);
        cachedRevision = { sessionId: result.sessionId, revision: result.revision };

        // Store session info for future syncs
        setReviewSessionId(result.sessionId);
        addToSessionHistory({
          id: result.sessionId,
          title: project.title,
          url: reviewUrl,
          password: result.password ?? undefined,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + ttlDays * 86_400_000).toISOString(),
        });

        setMode('done');

        // Patch audio URLs on the blob manifest (fire-and-forget fallback —
        // Unity may not forward audioUrls to the edge function during finalize)
        if (result._audioUrls && result._audioUrls.length > 0 && result.sessionId) {
          import('@/api/supabaseClient.ts').then(({ invokeEdgeFn }) =>
            invokeEdgeFn('team-upload', {
              action: 'patch-session-audio',
              sessionId: result.sessionId,
              audioUrls: result._audioUrls,
            })
          ).catch(() => {});
        }
      } else {
        // Standalone mode — upload via Supabase
        const reviewPayload = await buildReviewPayload();
        const { session, password } = await createSessionWithPassword(reviewPayload, project.title, ttlDays);

        const url = `${REVIEW_BASE_URL}?session=${session.id}`;
        setShareUrl(url);
        setSharePassword(password);
        setRevision(session.revision ?? 1);

        // Store in Zustand + localStorage
        setReviewSessionId(session.id);
        addToSessionHistory({
          id: session.id,
          title: project.title,
          url,
          password,
          createdAt: session.created_at,
          expiresAt: session.expires_at,
        });

        setMode('done');
      }
    } catch (err) {
      console.error('Failed to create session:', err);
      setError(err instanceof Error ? err.message : t('errorFailedToCreateSession'));
    } finally {
      setIsBusy(false);
    }
  }, [project, ttlDays, buildReviewPayload, setReviewSessionId, t]);

  const handleUpdate = useCallback(async () => {
    if (!project) return;
    if (interceptEmbeddedSignInForSharing()) return;

    setIsBusy(true);
    setError(null);

    try {
      if (capabilities.bridge) {
        // Embedded mode — sync via Unity team pipeline
        const syncStart = performance.now();
        console.log('[ShareDialog] handleUpdate: starting embedded sync...');
        const { requestTeamUpdate } = await import('@/bridge/embeddedBridge.ts');
        const result = await requestTeamUpdate();
        console.log(`[ShareDialog] handleUpdate: sync complete in ${(performance.now() - syncStart).toFixed(0)}ms`);

        // Password from bridge result (Unity always sends it), then store/localStorage fallback
        const storedPw = result.password
          ?? teamSharePassword
          ?? (result.sessionId ? getStoredPassword(result.sessionId) : null);

        // Use ?session= URL for reviewers (not ?team= which requires app auth)
        const updateReviewUrl = result.sessionId
          ? `${REVIEW_BASE_URL}?session=${result.sessionId}`
          : result.teamUrl;
        setShareUrl(updateReviewUrl);
        setSharePassword(storedPw);
        setRevision(result.revision);
        if (result.sessionId) cachedRevision = { sessionId: result.sessionId, revision: result.revision };
        setWasSynced(true);
        setMode('done');

        // Persist password to localStorage so it's available on next dialog open
        if (storedPw && result.sessionId) {
          addToSessionHistory({
            id: result.sessionId,
            title: project.title,
            url: updateReviewUrl,
            password: storedPw,
            createdAt: new Date().toISOString(),
            expiresAt: '', // unknown on update, but entry will be refreshed on next create
          });
        }

        // Patch audio URLs on the blob manifest (fire-and-forget fallback)
        if (result._audioUrls && result._audioUrls.length > 0 && result.sessionId) {
          import('@/api/supabaseClient.ts').then(({ invokeEdgeFn }) =>
            invokeEdgeFn('team-upload', {
              action: 'patch-session-audio',
              sessionId: result.sessionId,
              audioUrls: result._audioUrls,
            })
          ).catch(() => {});
        }
      } else {
        // Standalone mode
        if (!reviewSessionId) return;

        const reviewPayload = await buildReviewPayload();
        const result = await updateSessionProject(reviewSessionId, reviewPayload, undefined, revision);

        const url = `${REVIEW_BASE_URL}?session=${reviewSessionId}`;
        const storedPw = getStoredPassword(reviewSessionId);

        setShareUrl(url);
        setSharePassword(storedPw);
        setRevision(result.revision);
        setWasSynced(true);
        setMode('done');
      }
    } catch (err) {
      console.error('Failed to sync session:', err);
      const status = (err as any).status;
      if (status === 409) {
        setError(t('errorConflict'));
      } else {
        setError(err instanceof Error ? err.message : t('errorFailedToSyncSession'));
      }
      setMode('sync-error');
    } finally {
      setIsBusy(false);
    }
  }, [project, reviewSessionId, revision, buildReviewPayload]);

  const handleCreateNew = useCallback(async () => {
    if (!project) return;
    if (interceptEmbeddedSignInForSharing()) return;

    setIsBusy(true);
    setError(null);

    try {
      if (capabilities.bridge) {
        // Embedded mode — revoke old, clear manifest, upload fresh
        const oldSessionId = useProjectStore.getState().teamSessionId;
        if (oldSessionId) {
          const { revokeSession } = await import('@/api/reviewApi.ts');
          await revokeSession(oldSessionId);
        }

        // Clear Unity manifest so next upload is fresh
        const { enqueueSendToNative, requestTeamUpload } = await import('@/bridge/embeddedBridge.ts');
        enqueueSendToNative('clearTeamManifest', {});

        // Clear local state
        setReviewSessionId(null);
        useCommentStore.setState({ comments: [], approvals: [] });

        // Trigger new upload
        const createNewStart = performance.now();
        console.log('[ShareDialog] handleCreateNew: starting embedded share...');
        const result = await requestTeamUpload(ttlDays);
        console.log(`[ShareDialog] handleCreateNew: share complete in ${(performance.now() - createNewStart).toFixed(0)}ms`);

        // Use ?session= URL for reviewers (not ?team= which requires app auth)
        const relinkReviewUrl = `${REVIEW_BASE_URL}?session=${result.sessionId}`;
        setShareUrl(relinkReviewUrl);
        setSharePassword(result.password);
        setRevision(result.revision);
        cachedRevision = { sessionId: result.sessionId, revision: result.revision };

        setReviewSessionId(result.sessionId);
        addToSessionHistory({
          id: result.sessionId,
          title: project.title,
          url: relinkReviewUrl,
          password: result.password ?? undefined,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + ttlDays * 86_400_000).toISOString(),
        });

        setMode('done');

        // Patch audio URLs on the blob manifest (fire-and-forget fallback)
        if (result._audioUrls && result._audioUrls.length > 0 && result.sessionId) {
          import('@/api/supabaseClient.ts').then(({ invokeEdgeFn }) =>
            invokeEdgeFn('team-upload', {
              action: 'patch-session-audio',
              sessionId: result.sessionId,
              audioUrls: result._audioUrls,
            })
          ).catch(() => {});
        }
      } else {
        // Standalone mode — detach from current session, clear review data, create new
        setReviewSessionId(null);
        useUndoStore.getState().pushSnapshot(); // allow undo
        useCommentStore.setState({ comments: [], approvals: [] });

        const reviewPayload = await buildReviewPayload();
        const { session, password } = await createSessionWithPassword(reviewPayload, project.title, ttlDays);

        const url = `${REVIEW_BASE_URL}?session=${session.id}`;
        setShareUrl(url);
        setSharePassword(password);
        setRevision(session.revision ?? 1);

        setReviewSessionId(session.id);
        addToSessionHistory({
          id: session.id,
          title: project.title,
          url,
          password,
          createdAt: session.created_at,
          expiresAt: session.expires_at,
        });

        setMode('done');
      }
    } catch (err) {
      console.error('Failed to create new session:', err);
      setError(err instanceof Error ? err.message : t('errorFailedToCreateSession'));
      setMode('create');
    } finally {
      setIsBusy(false);
    }
  }, [project, ttlDays, teamAccessUrl, buildReviewPayload, setReviewSessionId, t]);

  const handleCopy = useCallback(async (textOverride?: string) => {
    const text = textOverride ?? shareUrl;
    if (!text) return;

    // In embedded mode, navigator.clipboard is unavailable (file:// is not a secure context)
    // and execCommand('copy') doesn't reach the iOS system clipboard from WKWebView.
    // Route through the native bridge so Unity can use UIPasteboard directly.
    if (capabilities.bridge) {
      const { enqueueSendToNative } = await import('@/bridge/embeddedBridge.ts');
      enqueueSendToNative('copyToClipboard', { text });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  if (!open) return null;

  const configured = capabilities.bridge || isSupabaseConfigured();

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('shareForReviewTitle')}</h3>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        {!configured ? (
          <div className={styles.body}>
            <p className={styles.warning}>
              {t('supabaseNotConfigured')}
            </p>
          </div>
        ) : mode === 'checking' ? (
          <div className={styles.body}>
            <p className={styles.description}>{t('checkingSession')}</p>
          </div>
        ) : mode === 'done' ? (
          <div className={styles.body}>
            {wasSynced ? (
              <p className={styles.success}>{t('projectUpdated')}</p>
            ) : !resumedExisting ? (
              <p className={styles.success}>{t('reviewLinkCreated')}</p>
            ) : null}

            <label className={styles.label}>{t('linkLabel')}</label>
            <div className={styles.urlRow}>
              <a
                className={styles.urlInput}
                href={shareUrl ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => {
                  // Select text on click for easy copying; let cmd/ctrl-click open normally
                  if (!e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    const range = document.createRange();
                    range.selectNodeContents(e.currentTarget);
                    const sel = window.getSelection();
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                  }
                }}
              >{shareUrl ?? ''}</a>
              <button className={styles.copyBtn} onClick={() => handleCopy()}>
                {t('copyButton')}
              </button>
            </div>

            {sharePassword && (
              <>
                <label className={styles.label}>{t('passwordLabel')}</label>
                <div className={styles.urlRow}>
                  <input
                    className={styles.passwordDisplay}
                    value={sharePassword}
                    readOnly
                    onClick={e => (e.target as HTMLInputElement).select()}
                  />
                  <button className={styles.copyBtn} onClick={() => handleCopy(sharePassword)}>
                    {t('copyButton')}
                  </button>
                </div>
              </>
            )}

            <button
              className={styles.shareBtn}
              onClick={() => handleCopy(
                sharePassword
                  ? `${shareUrl}\n\nPassword:\n${sharePassword}`
                  : shareUrl ?? ''
              )}
            >
              {copied ? t('copiedConfirmation') : sharePassword ? t('copyLinkAndPassword') : t('copyLinkOnly')}
            </button>

            <p className={styles.hint}>
              {wasSynced
                ? t('hintSynced', { revision })
                : resumedExisting
                  ? t('hintExistingSession', { revision })
                  : t('hintNewSession', { days: ttlDays })
              }
            </p>
            {resumedExisting && !wasSynced && (
              <button
                className={styles.shareBtn}
                onClick={() => { setMode('syncing'); handleUpdate(); }}
                disabled={isBusy}
              >
                {isBusy ? t('updatingProject') : t('updateProjectButton')}
              </button>
            )}
          </div>
        ) : mode === 'syncing' ? (
          <div className={styles.body}>
            <p className={styles.description}>{t('syncingLatestChanges')}</p>
            {progressMsg && <p className={styles.progressMsg}>{progressMsg}</p>}
          </div>
        ) : mode === 'sync-error' ? (
          <div className={styles.body}>
            <p className={styles.description}>{t('syncStopped')}</p>
            {error && <p className={styles.error}>{error}</p>}
            <button
              className={styles.shareBtn}
              onClick={() => { setError(null); setMode('syncing'); handleUpdate(); }}
              disabled={isBusy}
            >
              {t('retryButton')}
            </button>
            <button className={styles.secondaryBtn} onClick={onClose}>
              {t('closeButton')}
            </button>
          </div>
        ) : mode === 'confirm-new' ? (
          <div className={styles.body}>
            <p className={styles.description}>
              {t('confirmNewDescription1')}
            </p>
            <p className={styles.description}>
              {t('confirmNewDescription2')}
            </p>

            <label className={styles.label}>{t('linkExpirationLabel')}</label>
            <div className={styles.ttlRow}>
              {TTL_OPTIONS.map(opt => (
                <button
                  key={opt.days}
                  className={`${styles.ttlBtn} ${ttlDays === opt.days ? styles.ttlBtnActive : ''}`}
                  onClick={() => setTtlDays(opt.days)}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <button
              className={styles.shareBtn}
              onClick={handleCreateNew}
              disabled={isBusy || !project}
            >
              {isBusy ? t('creatingNewSession') : t('createNewSessionButton')}
            </button>

            <button
              className={styles.secondaryBtn}
              onClick={() => setMode('create')}
            >
              {t('cancelButton')}
            </button>
          </div>
        ) : (
          /* mode === 'create' */
          <div className={styles.body}>
            <p className={styles.description}>
              {t('createDescription')}
            </p>

            <label className={styles.label}>{t('linkExpirationLabel')}</label>
            <div className={styles.ttlRow}>
              {TTL_OPTIONS.map(opt => (
                <button
                  key={opt.days}
                  className={`${styles.ttlBtn} ${ttlDays === opt.days ? styles.ttlBtnActive : ''}`}
                  onClick={() => setTtlDays(opt.days)}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>

            {error && <p className={styles.error}>{error}</p>}

            {isBusy && progressMsg && (
              <p className={styles.progressMsg}>{progressMsg}</p>
            )}

            {showRewardHint && (
              <p className={styles.rewardHint}>
                {t('rewardHint')}
              </p>
            )}

            <button
              className={styles.shareBtn}
              onClick={handleCreate}
              disabled={isBusy || !project}
            >
              {isBusy ? t('creatingNewSession') : t('createReviewLinkButton')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
